import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Facebook, Linkedin, Link2, Mail, Share2, Twitter } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SITE = "https://forethehellofitmedia.com";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const useArticleSEO = (article) => {
  useEffect(() => {
    if (!article) return undefined;
    const prevTitle = document.title;
    const url = `${SITE}/news/${article.slug}`;
    const title = article.seo_title || `${article.title} | FORE Originals`;
    const desc = article.seo_description || article.subtitle || "Original editorial from Fore the Hell of It Media.";
    const socialTitle = article.social_title || article.title;
    const socialDesc = article.social_description || desc;
    const image = article.social_url ? `${SITE}${article.social_url}` : article.hero_url ? `${SITE}${article.hero_url}` : `${SITE}/logo-512.png`;

    document.title = title;
    setMeta("name", "description", desc);
    setMeta("property", "og:type", "article");
    setMeta("property", "og:title", socialTitle);
    setMeta("property", "og:description", socialDesc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("property", "og:site_name", "Fore the Hell of It Media");
    setMeta("property", "article:published_time", article.published_at);
    setMeta("property", "article:author", article.author);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", socialTitle);
    setMeta("name", "twitter:description", socialDesc);
    setMeta("name", "twitter:image", image);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);

    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.id = "article-jsonld";
    jsonLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: desc,
      image,
      datePublished: article.published_at,
      author: { "@type": "Organization", name: article.author || "FORE Staff" },
      publisher: {
        "@type": "Organization",
        name: "Fore the Hell of It Media",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/logo-512.png` },
      },
      mainEntityOfPage: url,
    });
    document.head.appendChild(jsonLd);
    return () => {
      document.title = prevTitle;
      document.getElementById("article-jsonld")?.remove();
    };
  }, [article]);
};

const ShareBar = ({ article, testidPrefix }) => {
  const url = `${SITE}/news/${article.slug}`;
  const text = article.social_title || article.title;
  const encoded = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  const links = [
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`, Icon: Facebook },
    { id: "x", label: "X", href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedText}`, Icon: Twitter },
    { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`, Icon: Linkedin },
    { id: "email", label: "Email", href: `mailto:?subject=${encodedText}&body=${encoded}`, Icon: Mail },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.", { duration: 2500 });
    } catch {
      toast.error("Could not copy link.");
    }
  };
  const nativeShare = async () => {
    try {
      await navigator.share({ title: text, text: article.subtitle || text, url });
    } catch {
      /* user dismissed */
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testidPrefix}-share`}>
      {links.map(({ id, label, href, Icon }) => (
        <a
          key={id}
          href={href}
          target={id === "email" ? undefined : "_blank"}
          rel="noopener noreferrer"
          aria-label={`Share on ${label}`}
          data-testid={`${testidPrefix}-share-${id}`}
          className="grid h-10 w-10 place-items-center border border-neutral-300 text-neutral-600 transition-colors duration-300 hover:border-neutral-900 hover:text-neutral-900"
        >
          <Icon className="h-4 w-4" />
        </a>
      ))}
      <button
        onClick={copy}
        aria-label="Copy link"
        data-testid={`${testidPrefix}-share-copy`}
        className="grid h-10 w-10 place-items-center border border-neutral-300 text-neutral-600 transition-colors duration-300 hover:border-neutral-900 hover:text-neutral-900"
      >
        <Link2 className="h-4 w-4" />
      </button>
      {typeof navigator !== "undefined" && navigator.share && (
        <button
          onClick={nativeShare}
          aria-label="Share"
          data-testid={`${testidPrefix}-share-native`}
          className="grid h-10 w-10 place-items-center border border-neutral-300 text-neutral-600 transition-colors duration-300 hover:border-neutral-900 hover:text-neutral-900"
        >
          <Share2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export const Masthead = () => (
  <div className="flex flex-col items-center" data-testid="article-masthead">
    <Link to="/news" aria-label="Fore the Hell of It — Golf News">
      <img src="/logo-editorial.png" alt="Fore the Hell of It" className="h-20 w-auto sm:h-24" data-testid="article-masthead-logo" />
    </Link>
    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">Fore Originals</p>
  </div>
);

export default function NewsArticle() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["news-article", slug],
    queryFn: () => api.get(`/news/originals/by-slug/${slug}`).then((r) => r.data),
    retry: false,
  });
  const article = data?.original;
  const related = data?.related || [];
  useArticleSEO(article);

  return (
    <main className="bg-[#FBFAF7] text-neutral-900" data-testid="article-page">
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-32 md:pt-40">
        <Masthead />
        <div className="mt-10 border-t border-neutral-200 pt-8">
          <Link to="/news" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500 transition-colors duration-300 hover:text-neutral-900" data-testid="article-back">
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            Golf News
          </Link>
        </div>

        {isLoading && <p className="mt-16 font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">Loading…</p>}
        {isError && (
          <div className="mt-16 border border-dashed border-neutral-300 py-16 text-center" data-testid="article-missing">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">This story isn&apos;t available.</p>
            <Link to="/news" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-900 underline underline-offset-4">
              Back to Golf News <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {article && (
          <article>
            <header className="mt-12">
              <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em]">
                <span className="bg-neutral-900 px-2.5 py-1 font-bold text-white" data-testid="article-label">FORE Original</span>
                {article.series && (
                  <span className="border border-[#E4572E] px-2.5 py-1 font-bold text-[#E4572E]" data-testid="article-series">{article.series}</span>
                )}
                {article.category && <span className="text-neutral-500">{article.category}</span>}
              </div>
              {article.series_deck && (
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500" data-testid="article-series-deck">{article.series_deck}</p>
              )}
              <h1 className="mt-5 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl" data-testid="article-title">
                {article.title}
              </h1>
              {article.subtitle && (
                <p className="mt-5 text-lg leading-relaxed text-neutral-600 md:text-xl" data-testid="article-deck">{article.subtitle}</p>
              )}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-neutral-200 py-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-600" data-testid="article-byline">
                  By {article.author} · {fmtDate(article.published_at)}
                </p>
                <ShareBar article={article} testidPrefix="article-top" />
              </div>
            </header>

            {article.hero_url && (
              <figure className="mt-10">
                <img src={article.hero_url} alt={article.title} className="w-full" data-testid="article-hero" />
              </figure>
            )}

            <div
              className="article-body mt-10 text-[1.05rem] leading-[1.85] text-neutral-800 md:text-lg"
              data-testid="article-body"
              dangerouslySetInnerHTML={{ __html: article.body }}
            />

            {article.sources?.length > 0 && (
              <section className="mt-12 border-t border-neutral-200 pt-8" data-testid="article-sources">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Sources &amp; References</p>
                <ul className="mt-4 space-y-2">
                  {article.sources.map((s, i) => (
                    <li key={i}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`article-source-${i}`} className="group inline-flex items-center gap-2 text-sm text-neutral-700 underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 hover:text-neutral-900 hover:decoration-neutral-900">
                        {s.label}
                        <ArrowRight className="h-3 w-3 -rotate-45 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {article.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2" data-testid="article-tags">
                {article.tags.map((t) => (
                  <span key={t} className="border border-neutral-300 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">{t}</span>
                ))}
              </div>
            )}

            <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-neutral-200 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Share this story</p>
              <ShareBar article={article} testidPrefix="article-bottom" />
            </div>
          </article>
        )}

        {related.length > 0 && (
          <section className="mt-16 border-t-2 border-neutral-900 pt-10" data-testid="article-related">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">More FORE Originals</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/news/${r.slug}`} data-testid={`related-${r.id}`} className="group">
                  {r.series && <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E4572E]">{r.series}</p>}
                  <h3 className="mt-2 font-display text-lg font-extrabold uppercase leading-tight tracking-tight text-neutral-900 transition-colors duration-300 group-hover:text-[#E4572E]">
                    {r.title}
                  </h3>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">{fmtDate(r.published_at)}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
